<!-- src\components\admin\layout\AdminHeader.vue -->
<template>
	<header class="admn_header">
		<button class="admn_menu-toggle" @click="handleToggleMenu" aria-label="Свернуть" title="Свернуть">
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
				<path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
			</svg>
		</button>

		<div class="admn_header-right">
			<!-- Уведомления -->
			<div class="admn_notifications" ref="notificationsContainer">
				<button
					class="admn_notification-btn"
					:class="{ 'admn_notification-btn--active': safeNotificationsCount > 0 || notificationsOpen }"
					title="Уведомления"
					:aria-expanded="notificationsOpen"
					@click="toggleNotifications"
				>
					<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
						<path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					<span v-if="safeNotificationsCount > 0" class="admn_notification-badge">
						{{ notificationsBadge }}
					</span>
				</button>

				<transition name="admn-notifications">
					<div
						v-if="notificationsOpen"
						class="admn_notifications-dropdown"
						role="menu"
						@click.stop
					>
						<div class="admn_notifications-header">
							<span>Уведомления</span>
							<button
								v-if="notificationsListToShow.length"
								type="button"
								class="admn_notifications-clear"
								@click="clearNotifications"
							>
								Очистить
							</button>
						</div>

						<div v-if="notificationsLoading" class="admn_notifications-state">
							Загрузка...
						</div>
						<div v-else-if="notificationsError" class="admn_notifications-state admn_notifications-state--error">
							{{ notificationsError }}
						</div>
						<div v-else-if="!notificationsListToShow.length" class="admn_notifications-state">
							Новых уведомлений нет.
						</div>
						<ul v-else class="admn_notifications-list">
							<li
								v-for="item in notificationsListToShow"
								:key="item.id"
								class="admn_notifications-item"
								:class="{ 'is-unread': !item.read }"
							>
								<div class="admn_notifications-item-title">
									{{ item.title || 'Уведомление' }}
								</div>
								<div class="admn_notifications-item-text">
									{{ item.message || '' }}
								</div>
								<div class="admn_notifications-item-time">
									{{ formatNotificationTime(item.timestamp) }}
								</div>
							</li>
						</ul>
					</div>
				</transition>
			</div>

			<!-- Профиль -->
			<button class="admn_profile-btn" @click="openProfileModal">
				<img class="admn_avatar" :src="avatarUrl" alt="Avatar" />
				<div class="admn_profile-info">
					<span class="admn_username">{{ displayName }}</span>
					<span class="admn_role" :class="roleClass">{{ roleLabel }}</span>
				</div>
				<svg class="admn_chevron" width="16" height="16" viewBox="0 0 24 24" fill="none">
					<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</button>
		</div>
	</header>

	<!-- Модальное окно профиля -->
	<Teleport to="body">
		<Transition name="modal-fade">
			<div v-if="isProfileModalOpen" class="admn_modal-overlay" @click.self="closeProfileModal">
				<div class="admn_modal">
					<div class="admn_modal-header">
						<h3>Профиль</h3>
						<button class="admn_modal-close" @click="closeProfileModal">&times;</button>
					</div>
					<div class="admn_modal-body">
						<div class="admn_profile-modal-content">
							<div class="admn_profile-avatar-section">
								<img :src="avatarUrl" alt="Avatar" />
								<label class="admn_avatar-upload">
									<input
										type="file"
										accept="image/*"
										:disabled="isAvatarUploading"
										@change="handleAvatarChange"
									/>
									<span>{{ isAvatarUploading ? 'Загрузка...' : 'Сменить аватар' }}</span>
								</label>
								<div v-if="avatarError" class="admn_error">{{ avatarError }}</div>
							</div>
							<div class="admn_profile-details">
								<div class="admn_detail-row">
									<span class="admn_detail-label">Имя</span>
									<span class="admn_detail-value">{{ displayName }}</span>
								</div>
								<div class="admn_detail-row">
									<span class="admn_detail-label">Email</span>
									<span class="admn_detail-value">{{ displayEmail }}</span>
								</div>
								<div class="admn_detail-row">
									<span class="admn_detail-label">Роль</span>
									<span class="admn_role" :class="roleClass">{{ roleLabel }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/useAppStore'
import { api } from '@/services'
import adminRolesService from '@/services/adminRoles'
import { getAvatarUrl } from '@/utils/imageUrl'

const toggleMenu = inject('toggleMenu')
const appStore = useAppStore()
const { user, notifications: storeNotifications } = storeToRefs(appStore)
const adminProfile = ref(null)
const isProfileModalOpen = ref(false)
const isAvatarUploading = ref(false)
const avatarError = ref('')
const notificationsOpen = ref(false)
const notificationsContainer = ref(null)
const notificationsLoading = ref(false)
const notificationsError = ref('')
const notificationsList = ref([])
const hasFetchedNotifications = ref(false)
const notificationsApiAvailable = ref(true)
const unreadCount = ref(null)
let notificationsPoller = null
const hasAccessToken = () => {
	if (typeof window === 'undefined') return false
	return !!localStorage.getItem('access_token')
}

const NOTIFICATIONS_ENDPOINT =
	import.meta.env?.VITE_NOTIFICATIONS_ENDPOINT ||
	process.env.VUE_APP_NOTIFICATIONS_ENDPOINT ||
	'/notifications'
const NOTIFICATIONS_COUNT_ENDPOINT = `${NOTIFICATIONS_ENDPOINT}/unread-count`
const NOTIFICATIONS_READ_ALL_ENDPOINT = `${NOTIFICATIONS_ENDPOINT}/read-all`

const handleToggleMenu = () => {
	if (toggleMenu) toggleMenu()
}

const loadAdminProfile = async () => {
	try {
		const data = await adminRolesService.getCurrentUser()
		adminProfile.value = data?.user || data?.data?.user || data?.data || data
	} catch (error) {
		const status = error?.status || error?.response?.status
		if (status !== 401 && status !== 403) {
			console.error('[AdminHeader] Failed to load admin profile:', error)
		}
	}
}

const loadAdminPermissions = async () => {
	try {
		const data = await adminRolesService.getMyPermissions()
		const permissions = Array.isArray(data?.permissions) ? data.permissions : []
		appStore.setPermissions(permissions)
	} catch (error) {
		const status = error?.status || error?.response?.status
		if (status !== 401 && status !== 403) {
			console.error('[AdminHeader] Failed to load admin permissions:', error)
		}
		appStore.setPermissions([])
	}
}

const displayName = computed(() => {
	const profile = adminProfile.value || user.value
	if (!profile) return 'Администратор'
	return profile.fullName || profile.full_name || 
		   [profile.firstName || profile.first_name, profile.lastName || profile.last_name].filter(Boolean).join(' ') ||
		   profile.email || 'Администратор'
})

const displayEmail = computed(() => adminProfile.value?.email || user.value?.email || '-')
const isSuperAdmin = computed(() => {
	const email = adminProfile.value?.email || user.value?.email || ''
	return String(email).trim().toLowerCase() === 'super@metall.edu'
})

const roleMeta = computed(() => {
	if (isSuperAdmin.value) {
		return { label: 'super admin', className: 'role--super-admin' }
	}
	const roleRaw = adminProfile.value?.role?.displayName || 
	                adminProfile.value?.role?.name || 
	                adminProfile.value?.role || 
	                user.value?.roles?.[0] || ''
	const roleText = String(roleRaw).toLowerCase()

	if (roleText.includes('moderator') || roleText.includes('модера')) {
		return { label: 'Модератор', className: 'role--moderator' }
	}
	if (roleText.includes('admin') || roleText.includes('админ')) {
		return { label: 'Админ', className: 'role--admin' }
	}
	return { label: roleRaw || 'Профиль', className: 'role--neutral' }
})

const roleLabel = computed(() => roleMeta.value.label)
const roleClass = computed(() => roleMeta.value.className)

const avatarUrl = computed(() => {
	const path = adminProfile.value?.avatarUrl || adminProfile.value?.avatar_url || 
	             adminProfile.value?.avatar || user.value?.avatarUrl || 
	             user.value?.avatar_url || user.value?.avatar
	return getAvatarUrl(path)
})

const notificationsListToShow = computed(() => {
	if (notificationsApiAvailable.value && hasFetchedNotifications.value) {
		return notificationsList.value
	}
	if (notificationsList.value.length > 0) {
		return notificationsList.value
	}
	const list = Array.isArray(storeNotifications?.value) ? storeNotifications.value : []
	return list.map((item, index) => normalizeNotification(item, index))
})

const safeNotificationsCount = computed(() => {
	if (typeof unreadCount.value === 'number') {
		return unreadCount.value
	}
	const list = notificationsListToShow.value
	if (!list.length) return 0
	const unreadFromList = list.filter(item => !item.read).length
	const hasReadFlags = list.some(item => item.read)
	return hasReadFlags ? unreadFromList : (unreadFromList || list.length)
})

const notificationsBadge = computed(() => {
	const count = safeNotificationsCount.value
	return count > 99 ? '99+' : count
})

const formatNotificationTime = timestamp => {
	if (!timestamp) return ''
	const date = new Date(timestamp)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	})
}

const normalizeNotification = (item, index = 0) => {
	const raw = item || {}
	const id = raw.id || raw._id || raw.notificationId || raw.uuid || `local-${index}`
	const title = raw.title || raw.subject || raw.name || ''
	const message = raw.message || raw.text || raw.body || raw.description || ''
	const timestamp =
		raw.timestamp || raw.createdAt || raw.created_at || raw.time || Date.now()
	const read =
		Boolean(raw.read || raw.isRead || raw.readAt || raw.read_at || raw.seenAt)

	return {
		id,
		title,
		message,
		timestamp,
		read,
		type: raw.type || raw.level || 'info',
	}
}

const extractNotifications = payload => {
	if (!payload) return []
	const candidate =
		payload.data?.notifications ||
		payload.notifications ||
		payload.data?.items ||
		payload.items ||
		payload.data?.results ||
		payload.results ||
		payload.data ||
		payload

	return Array.isArray(candidate) ? candidate : []
}

const extractUnreadCount = payload => {
	const count =
		payload?.data?.unreadCount ||
		payload?.data?.count ||
		payload?.unreadCount ||
		payload?.count ||
		payload?.data?.meta?.unreadCount ||
		payload?.data?.meta?.count
	return Number.isFinite(Number(count)) ? Number(count) : null
}

const handleNotificationsApiError = error => {
	const status = error?.status || error?.response?.status
	if (status === 404 || status === 501) {
		notificationsApiAvailable.value = false
		stopNotificationsPolling()
		return true
	}
	if (status === 401 || status === 403) {
		notificationsApiAvailable.value = false
		notificationsOpen.value = false
		unreadCount.value = 0
		stopNotificationsPolling()
		return true
	}
	return false
}

const fetchNotifications = async () => {
	if (!notificationsApiAvailable.value) {
		notificationsList.value = []
		hasFetchedNotifications.value = true
		return
	}
	if (!hasAccessToken()) {
		notificationsList.value = []
		unreadCount.value = 0
		hasFetchedNotifications.value = true
		return
	}

	notificationsLoading.value = true
	notificationsError.value = ''
	try {
		const data = await api.get(NOTIFICATIONS_ENDPOINT)
		const items = extractNotifications(data)
		const normalized = items.map((item, index) => normalizeNotification(item, index))
		notificationsList.value = normalized

		const count = extractUnreadCount(data)
		if (count !== null) {
			unreadCount.value = count
		} else if (normalized.length) {
			unreadCount.value = normalized.filter(item => !item.read).length
		}
	} catch (error) {
		const disabled = handleNotificationsApiError(error)
		notificationsError.value = disabled ? '' : (error?.message || 'Не удалось загрузить уведомления')
	} finally {
		hasFetchedNotifications.value = true
		notificationsLoading.value = false
	}
}

const fetchNotificationsCount = async () => {
	if (!notificationsApiAvailable.value) return
	if (!hasAccessToken()) {
		unreadCount.value = 0
		return
	}
	try {
		const data = await api.get(NOTIFICATIONS_COUNT_ENDPOINT)
		const count = extractUnreadCount(data)
		if (count !== null) {
			unreadCount.value = count
		}
	} catch (error) {
		handleNotificationsApiError(error)
	}
}

const startNotificationsPolling = () => {
	if (!hasAccessToken()) return
	if (notificationsPoller) return
	notificationsPoller = setInterval(() => {
		if (!hasAccessToken()) return
		fetchNotificationsCount()
		if (notificationsOpen.value) {
			fetchNotifications()
		}
	}, 30000)
}

const stopNotificationsPolling = () => {
	if (notificationsPoller) {
		clearInterval(notificationsPoller)
		notificationsPoller = null
	}
}

const toggleNotifications = async () => {
	notificationsOpen.value = !notificationsOpen.value
	if (notificationsOpen.value) {
		await fetchNotifications()
	}
}

const clearNotifications = async () => {
	notificationsList.value = []
	unreadCount.value = 0
	appStore.clearNotifications()
	if (!notificationsApiAvailable.value) return

	try {
		await api.post(NOTIFICATIONS_READ_ALL_ENDPOINT)
	} catch (error) {
		handleNotificationsApiError(error)
	}
}

const handleOutsideNotificationsClick = event => {
	if (!notificationsOpen.value) return
	if (!notificationsContainer.value?.contains(event.target)) {
		notificationsOpen.value = false
	}
}

const handleNotificationsKeydown = event => {
	if (event.key === 'Escape') {
		notificationsOpen.value = false
	}
}

const openProfileModal = async () => {
	if (!adminProfile.value) await loadAdminProfile()
	isProfileModalOpen.value = true
	document.body.style.overflow = 'hidden'
}

const closeProfileModal = () => {
	isProfileModalOpen.value = false
	document.body.style.overflow = ''
	avatarError.value = ''
}

const handleAvatarChange = async (event) => {
	const file = event.target.files?.[0]
	if (!file) return

	if (!file.type.startsWith('image/')) {
		avatarError.value = 'Выберите файл изображения'
		event.target.value = ''
		return
	}

	avatarError.value = ''
	isAvatarUploading.value = true
	try {
		const data = await adminRolesService.uploadMyAvatar(file)
		const avatarPath = data?.avatarUrl || data?.avatar_url || data?.path || data?.url ||
		                   data?.data?.avatarUrl || data?.data?.avatar_url || data?.data?.path || data?.data?.url

		if (avatarPath) {
			adminProfile.value = { ...(adminProfile.value || {}), avatarUrl: avatarPath }
			appStore.setUser({ ...(user.value || {}), avatarUrl: avatarPath })
			appStore.persistUser()
		} else {
			avatarError.value = 'Не удалось получить ссылку на аватар'
		}
	} catch (error) {
		avatarError.value = error?.message || 'Ошибка загрузки аватара'
	} finally {
		isAvatarUploading.value = false
		event.target.value = ''
	}
}

onMounted(() => {
	loadAdminProfile()
	loadAdminPermissions()
	if (hasAccessToken()) {
		fetchNotificationsCount()
		startNotificationsPolling()
	} else {
		unreadCount.value = 0
	}
	document.addEventListener('click', handleOutsideNotificationsClick)
	window.addEventListener('keydown', handleNotificationsKeydown)
})

onBeforeUnmount(() => {
	stopNotificationsPolling()
	document.removeEventListener('click', handleOutsideNotificationsClick)
	window.removeEventListener('keydown', handleNotificationsKeydown)
})
</script>

<style scoped>
.admn_header {
	position: sticky;
	top: 0;
	margin: 0 0 0 var(--admin-sidebar-offset);
	height: var(--admin-header-height, 84px);
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0;
	/* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); */
	z-index: 100;
	transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-collapsed .admn_header {
	margin-left: var(--admin-sidebar-offset-collapsed);
}

.admn_menu-toggle {
	width: 48px;
	height: 48px;
	border: none;
	background: #f5f7fa;
	border-radius: 12px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #64748b;
	transition: all 0.2s ease;
	margin-left: 0;
	transform: translateX(-20px);
}

.admn_menu-toggle:hover {
	background: #e2e8f0;
	color: #1e293b;
}

.admn_header-right {
	display: flex;
	align-items: center;
	gap: 18px;
}

.admn_notifications {
	position: relative;
}

.admn_notification-btn {
	position: relative;
	width: 48px;
	height: 48px;
	border: none;
	background: #f5f7fa;
	border-radius: 12px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #64748b;
	transition: all 0.2s ease;
}

.admn_notification-btn:hover {
	background: #e2e8f0;
	color: #1e293b;
}

.admn_notification-btn--active {
	background: #e7effd;
	color: #1d4ed8;
}

.admn_notification-badge {
	position: absolute;
	top: 6px;
	right: 6px;
	background: #ef4444;
	color: white;
	font-size: 10px;
	font-weight: 600;
	min-width: 18px;
	height: 18px;
	border-radius: 9px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px solid white;
}

.admn_notifications-dropdown {
	position: absolute;
	top: calc(100% + 10px);
	right: 0;
	width: 320px;
	background: #ffffff;
	border: 1px solid rgba(148, 163, 184, 0.2);
	border-radius: 14px;
	box-shadow: 0 20px 48px rgba(15, 23, 42, 0.12);
	overflow: hidden;
	z-index: 200;
	max-height: 420px;
	overflow-y: auto;
}

.admn_notifications-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 16px;
	font-size: 14px;
	font-weight: 600;
	color: #1e293b;
	background: #f8fafc;
	border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.admn_notifications-clear {
	border: none;
	background: transparent;
	color: #2563eb;
	font-size: 12px;
	cursor: pointer;
	font-weight: 600;
}

.admn_notifications-clear:hover {
	color: #1d4ed8;
}

.admn_notifications-state {
	padding: 20px 16px;
	font-size: 13px;
	color: #64748b;
	text-align: center;
}

.admn_notifications-state--error {
	color: #dc2626;
	background: #fef2f2;
}

.admn_notifications-list {
	list-style: none;
	margin: 0;
	padding: 8px;
	display: grid;
	gap: 8px;
}

.admn_notifications-item {
	padding: 12px 12px;
	border-radius: 12px;
	background: #f8fafc;
	border: 1px solid transparent;
	transition: border-color 0.2s ease, background 0.2s ease;
}

.admn_notifications-item.is-unread {
	background: #ffffff;
	border-color: #dbeafe;
	box-shadow: 0 8px 18px rgba(59, 130, 246, 0.08);
}

.admn_notifications-item-title {
	font-size: 13px;
	font-weight: 600;
	color: #1e293b;
	margin-bottom: 4px;
}

.admn_notifications-item-text {
	font-size: 12px;
	color: #64748b;
	line-height: 1.4;
	margin-bottom: 8px;
}

.admn_notifications-item-time {
	font-size: 11px;
	color: #94a3b8;
}

.admn-notifications-enter-active,
.admn-notifications-leave-active {
	transition: all 0.2s ease;
}

.admn-notifications-enter-from,
.admn-notifications-leave-to {
	opacity: 0;
	transform: translateY(-8px);
}

.admn_profile-btn {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 10px 14px;
	border: none;
	background: #f5f7fa;
	border-radius: 14px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.admn_profile-btn:hover {
	background: #e2e8f0;
}

.admn_avatar {
	width: 44px;
	height: 44px;
	border-radius: 12px;
	object-fit: cover;
}

.admn_profile-info {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
}

.admn_username {
	font-family: 'Poppins', sans-serif;
	font-size: 15px;
	font-weight: 600;
	color: #1e293b;
}

.admn_role {
	font-size: 12px;
	font-weight: 600;
	padding: 3px 10px;
	border-radius: 12px;
}

.role--admin {
	background: #dbeafe;
	color: #2563eb;
}

.role--super-admin {
	background: #fee2e2;
	color: #b91c1c;
}

.role--moderator {
	background: #fef3c7;
	color: #d97706;
}

.role--neutral {
	background: #f1f5f9;
	color: #64748b;
}

.admn_chevron {
	color: #94a3b8;
	transition: transform 0.2s ease;
}

.admn_profile-btn:hover .admn_chevron {
	transform: translateY(2px);
}

/* Modal */
.admn_modal-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 20px;
}

.admn_modal {
	background: white;
	border-radius: 16px;
	width: 100%;
	max-width: 480px;
	box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
	overflow: hidden;
}

.admn_modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px 24px;
	border-bottom: 1px solid #f0f0f0;
}

.admn_modal-header h3 {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	color: #1e293b;
}

.admn_modal-close {
	width: 32px;
	height: 32px;
	border: none;
	background: #f5f5f5;
	border-radius: 8px;
	font-size: 20px;
	cursor: pointer;
	color: #666;
	transition: all 0.2s ease;
}

.admn_modal-close:hover {
	background: #fee2e2;
	color: #dc2626;
}

.admn_modal-body {
	padding: 24px;
}

.admn_profile-modal-content {
	display: flex;
	gap: 24px;
}

.admn_profile-avatar-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
}

.admn_profile-avatar-section img {
	width: 100px;
	height: 100px;
	border-radius: 16px;
	object-fit: cover;
	border: 3px solid #f0f0f0;
}

.admn_avatar-upload {
	padding: 8px 16px;
	background: #2563eb;
	color: white;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: background 0.2s ease;
}

.admn_avatar-upload:hover {
	background: #1d4ed8;
}

.admn_avatar-upload input {
	display: none;
}

.admn_error {
	color: #dc2626;
	font-size: 12px;
	text-align: center;
}

.admn_profile-details {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.admn_detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.admn_detail-label {
	font-size: 13px;
	color: #64748b;
}

.admn_detail-value {
	font-size: 14px;
	font-weight: 500;
	color: #1e293b;
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

.modal-fade-enter-from .admn_modal,
.modal-fade-leave-to .admn_modal {
	transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
	.admn_header {
		margin: 0 0 0 16px;
		padding: 0;
	}

	.menu-collapsed .admn_header {
		margin-left: 16px;
	}
	
	.admn_profile-info {
		display: none;
	}
	
	.admn_chevron {
		display: none;
	}
	
	.admn_profile-modal-content {
		flex-direction: column;
		align-items: center;
	}

	.admn_notifications-dropdown {
		width: 280px;
		right: -8px;
	}
}

</style>
